import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { MapPin } from "lucide-react";

interface CompactLocationBadgesProps {
  locationNames: string[];
  className?: string;
}

/**
 * Compact location display optimized for virtualized lists.
 * Shows locations inline or in a popover to maintain fixed heights.
 */
export function CompactLocationBadges({ locationNames, className }: CompactLocationBadgesProps) {
  const { t } = useTranslation();

  if (!locationNames || locationNames.length === 0) {
    return null;
  }

  // Single location - show inline (no truncate, allow wrap)
  if (locationNames.length === 1) {
    return (
      <div className={cn("flex items-start gap-1.5 text-xs text-muted-foreground", className)}>
        <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <span>{locationNames[0]}</span>
      </div>
    );
  }

  // 2+ locations - show count with popover (no height change)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="text"
          size="sm"
          className={cn("h-auto !p-0 text-xs text-muted-foreground hover:text-foreground align-middle", className)}
          onClick={(e) => e.stopPropagation()}
        >
          <MapPin className="h-4 w-4 mt-0.5 mr-1.5 flex-shrink-0" />
          <span className="truncate">
            {t("locations.locationsSelectedInline", { count: locationNames.length })}
          </span>
        </Button>
      </PopoverTrigger>

      <PopoverContent 
        className="w-80 max-w-[calc(100vw-2rem)] p-3" 
        align="start" 
        side="top"
        sideOffset={5}
      >
        <div className="space-y-2">          
          <ul className="list-disc list-outside pl-4 space-y-1 text-sm text-muted-foreground">
            {locationNames.map((name, index) => (
              <li key={index}>
                {name}
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}

